import { EventInput } from "@fullcalendar/core";
import { ResourceInput } from "@fullcalendar/resource";
import { createSelector } from "reselect";

import { EntityType } from "@mapmycustomers/shared";
import { Activity } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import SchedulerViewState from "@app/component/view/SchedulerView/types/SchedulerViewState";
import { isCurrentUserOwner } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { getSimpleConditionForField } from "@app/util/filters/utils";

import activityToEvent from "../util/activityToEvent";

const schedulerState = (state: RootState) => state.scene.activityScheduler;

export const getSchedulerViewState = createSelector(
  schedulerState,
  ({ viewState }): SchedulerViewState => viewState
);
export const getSchedulerActivities = createSelector(
  schedulerState,
  ({ activities }) => activities
);

export const isSchedulerViewLoading = createSelector(schedulerState, ({ loading }) => loading);
export const getSchedulerViewTotalFilteredRecordsCount = createSelector(
  schedulerState,
  ({ totalFilteredRecords }) => totalFilteredRecords
);
export const getSchedulerViewTotalRecordsCount = createSelector(
  schedulerState,
  ({ totalRecords }) => totalRecords
);

export const getSchedulerEvents = createSelector(
  getSchedulerActivities,
  (activities: Activity[]) => {
    const events: EventInput[] = activities.map((activity) => activityToEvent(activity));
    return events;
  }
);

export const getSchedulerResources = createSelector(
  isCurrentUserOwner,
  getSchedulerViewState,
  getUsers,
  (isCurrentUserOwner: boolean, viewState: SchedulerViewState, users: User[]) => {
    const assigneeFilter = getSimpleConditionForField(
      viewState.filter,
      ActivityFieldName.ASSIGNEE,
      EntityType.ACTIVITY
    );
    const teamFilter = getSimpleConditionForField(
      viewState.filter,
      ActivityFieldName.TEAM,
      EntityType.ACTIVITY
    );

    const teamUsers = teamFilter?.value
      ? users.filter(({ teams }) =>
          (teamFilter.value as Team["id"][] | undefined)?.some((teamId) =>
            teams.map(({ id }) => id).includes(teamId)
          )
        )
      : users;

    const filteredUsers = (
      assigneeFilter?.value
        ? teamUsers.filter((user) => assigneeFilter.value.includes(user.id))
        : teamUsers
    ).filter((user) => (!isCurrentUserOwner ? !isOwner(user) : true));

    const resources: ResourceInput[] = filteredUsers.map((user) => ({
      id: String(user.id),
      extendedProps: { user },
      title: user.fullName ?? user.username,
    }));
    return resources;
  }
);
